import { LegacyRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { v4 as uuidV4 } from 'uuid';

import { SelectCore } from './select-core';
import { getMenuItemRenderer, getNoMatchItemRenderer, menuItemsSorter } from './select-helpers';
import { SelectProps } from './select.types';
import { useSelectionWithSearch } from './use-selection-with-search';
import { FormHelperText, MenuItem } from '../..';
import { FilterComboBoxMenuItem, matchesSearch } from '../filter-combo-box/filter-combo-box-menu-item';

// eslint-disable-next-line max-lines-per-function
const SelectWithoutRef = <T extends MenuItem>(props: SelectProps<T>, ref: LegacyRef<HTMLDivElement>) => {
  const {
    data,
    onChange,
    open,
    onSearch: onSearchCallback,
    renderListItem,
    renderNoMatchesItem,
    defaultValue,
    value,
    defaultSearchTerm,
    errorProps,
    onScrollToBottom,
    onClearSelection: onClear,
    customSortFn,
    autoSort = true,
    ...rest
  } = props;
  const size = rest.containerSize ?? 'medium';

  const { current: id } = useRef(uuidV4());
  const [menuOpen, setMenuOpen] = useState(open);
  const { ref: inViewRef, inView } = useInView();

  useEffect(() => {
    if (inView) {
      onScrollToBottom?.();
    }
  }, [onScrollToBottom, inView]);

  const { searchTerm, selectedItem, onToggleItem, onClearSelection, onSearch } = useSelectionWithSearch({
    data,
    value,
    defaultValue,
    defaultSearchTerm,
    onChange,
    onSearchCallback,
    setMenuOpen,
    onClear,
  });

  const menuItemRenderer = getMenuItemRenderer(renderListItem);
  const noMatchMenuItemRenderer = getNoMatchItemRenderer(renderNoMatchesItem);

  const menuItems = useMemo(() => {
    let matchedItems = data.filter((item) => !searchTerm || matchesSearch(item, searchTerm));
    if (autoSort) {
      matchedItems = matchedItems.sort(customSortFn ?? menuItemsSorter);
    }
    return matchedItems.length > 0
      ? matchedItems.map((item) => {
          return (
            <FilterComboBoxMenuItem
              key={item.value}
              item={item}
              defaultSelection={selectedItem?.value === item.value}
              onSelect={onToggleItem}
              className={size === 'medium' ? 'text-sm' : 'px-5 py-2.5 text-base'}
              renderItem={menuItemRenderer}
            />
          );
        })
      : noMatchMenuItemRenderer();
  }, [
    data,
    noMatchMenuItemRenderer,
    searchTerm,
    selectedItem?.value,
    onToggleItem,
    size,
    menuItemRenderer,
    customSortFn,
    autoSort,
  ]);

  return (
    <>
      <SelectCore<T>
        ref={ref}
        id={`SelectI-${id}`}
        open={menuOpen}
        onMenuOpen={setMenuOpen}
        searchTerm={searchTerm}
        onSearch={onSearch}
        {...rest}
        selectedItem={selectedItem}
        onClearSelection={onClearSelection}
        value={value}
      >
        <div role={'menu'}>
          {menuItems}
          <div ref={inViewRef} />
        </div>
      </SelectCore>
      {errorProps && <FormHelperText {...{ ...errorProps, type: 'error', size: errorProps.size ?? size }} />}
    </>
  );
};

export const Select = forwardRef(SelectWithoutRef) as <T extends MenuItem>(
  props: SelectProps<T> & { ref?: LegacyRef<HTMLDivElement> }
) => JSX.Element;

export default Select;
